/*
 * Copyright (c) 2008-2019 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import store from "@/store"
import { EventBus } from "~/common/src/event-bus"

/* global log */

export { permissionCheckMixin }

const PERMISSIONS_LOADED_EVENT = "authorization:permissionsLoaded"
let checkPermissionsAndRedirectCallback = null

/**
 * Mixin for checking whether the user has the permissions required to enter a component.
 * Redirects to home page, if required permissions are not available.
 *
 * @param requiredPermissions
 * @returns {{beforeRouteEnter(*, *, *=): void}}
 */
const permissionCheckMixin = function (requiredPermissions) {
  return {
    beforeRouteEnter(to, from, next) {
      // destroy event listener if set - this should not happen
      if (checkPermissionsAndRedirectCallback != null) {
        log.error(
          "Event listener for " +
            PERMISSIONS_LOADED_EVENT +
            " found in beforeRouteEnter hook. Probably the hook is called multiple times."
        )
        debugger // todo use notifyProgrammingError instead
        EventBus.$off(PERMISSIONS_LOADED_EVENT, checkPermissionsAndRedirectCallback)
        checkPermissionsAndRedirectCallback = null
      }

      // if permissions are available, resolve directly - otherwise setup event listener
      if (store.getters["auth/permissionsLoaded"]) {
        log.debug("Verify the following required permissions are available: ", requiredPermissions)
        checkPermissionsAndRedirect(requiredPermissions, next)
      } else {
        log.debug(
          "Permissions are not yet loaded from server. Subscribing on " + PERMISSIONS_LOADED_EVENT + " to check later."
        )
        checkPermissionsAndRedirectCallback = () => checkPermissionsAndRedirect(requiredPermissions, next)
        EventBus.$on(PERMISSIONS_LOADED_EVENT, checkPermissionsAndRedirectCallback)
      }
    },
  }
}

/**
 * check presence of required permissions and continue routing
 *
 * @param requiredPermissions
 * @param next
 */
const checkPermissionsAndRedirect = function (requiredPermissions, next) {
  // destroy event listener if set - It is not required anymore after initial routing is finished
  if (checkPermissionsAndRedirectCallback != null) {
    log.debug(
      "Permissions have now finished loading. Unsubscribing from " + PERMISSIONS_LOADED_EVENT + " and performing check."
    )
    EventBus.$off(PERMISSIONS_LOADED_EVENT, checkPermissionsAndRedirectCallback)
    checkPermissionsAndRedirectCallback = null
  }

  // check permissions and route
  if (store.getters["auth/hasPermissions"](requiredPermissions)) {
    log.debug("Required permissions are available. Redirecting to requested page.")
    next()
    return
  } else {
    log.warn("Missing permission(s) ", requiredPermissions, ". Redirecting to homepage.")
  }

  next({ name: "home" })
}

/**
 * load permissions from local storage into store or -if not available- from server to store and local storage
 */
const loadPermissions = function () {
  // load old permissions from local storage for faster application startup
  let permissions = JSON.parse(window.localStorage.getItem("permissions"))
  if (permissions) {
    log.debug(
      "Detected permissions from previous session in local storage. Using those until most recent permissions have finished loading."
    )

    store.commit("auth/loadPermissions", permissions)
    store.commit("auth/permissionStatus", "loaded")
  }

  // Trigger the dispatch to load permissions *after* setting the old permissions.
  // This avoids any race condition, and ensures old permissions can never overwrite
  // newly loaded permissions.
  log.debug("Loading permissions from server.")
  store.dispatch("auth/loadPermissions").then((permissions) => {
    log.debug("Permissions loaded. Persisting in local storage.")
    localStorage.setItem("permissions", JSON.stringify(permissions))

    EventBus.safeTrigger(PERMISSIONS_LOADED_EVENT)
  })
}

const loadLicenses = function() {
  const currentUser = store.getters["user/userSettings"]
  log.debug("after login: ", currentUser)
  // pending: (move this somewhere else, e.g. in a file afterLoginActions
  //  load systemConfiguration, when done, load permissions, when done, load current user, when done:
  //  if admin: load all tenants OR retrieve selectedTenantId from localStorage, if non-admin: load my tenant
  //  when done loading tenant(s) load tenant's efficientLicense
  //  ensure this is all done before App stuff (i.e. App.vue) loads !!!!
  //  Move to separate file
  //  Maybe we can get rid of loading efficient licenses and/or permissions in BeforeRouteCheckGuards.js (test it out)
  //  Ensure everything that is necessary is updated when efficient license of "current" tenant changes
}

// event listeners to loadPermissions after login and clear local storage after logout
EventBus.$on("OAuth:login:finished", loadPermissions)
// EventBus.$on("OAuth:login:finished", loadLicenses)
EventBus.$on("OAuth:logout", () => {
  window.localStorage.removeItem("permissions")
})
